import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, MapPin, Calendar, ChevronLeft, ChevronRight, Award, Zap, Shield, Users, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Yerel müsabaka ve etkinlik görselleri
import musabaka1 from '../assets/musabaka/1.jpeg';
import musabaka2 from '../assets/musabaka/2.jpeg';
import musabaka3 from '../assets/musabaka/3.jpeg';
import musabaka4 from '../assets/musabaka/4.jpeg';

// Yerel drone varlıkları
import droneImg3 from '../assets/drone3.png';
import droneImg4 from '../assets/drone4.png';
import droneImg5 from '../assets/drone5.png';
import droneImg6 from '../assets/drone6.png';

interface SlideData {
  id: number;
  badge: string;
  badgeIcon: any;
  title: string;
  highlight: string;
  location: string;
  date: string;
  description: string;
  stats: { label: string; value: string }[];
  bgImage: string;
  droneAsset: string;
  accentColor: string;
  overlayTag: { title: string; subtitle: string };
}

const SLIDES: SlideData[] = [
  {
    id: 1,
    badge: "LİG MÜSABAKASI DERECE",
    badgeIcon: Trophy,
    title: "Kurtköy Pendik Spor Salonunda",
    highlight: "Drone Futbol Ligi Derecesi!",
    location: "Kurtköy Pendik Spor Salonu, İstanbul",
    date: "2026 Sezonu",
    description: "Kurtköy Pendik Spor Salonu'nda düzenlenen heyecan dolu Drone Futbol Ligi müsabakasında takımımız üstün bir mücadele sergileyerek derece elde etti ve kürsüdeki yerini aldı.",
    stats: [
      { label: "Müsabaka", value: "Drone Futbol Ligi" },
      { label: "Salon", value: "Kurtköy Pendik" },
      { label: "Sonuç", value: "Derece & Kürsü" }
    ],
    bgImage: droneImg3,
    droneAsset: musabaka1,
    accentColor: "#e65100",
    overlayTag: { title: "Drone Futbolu", subtitle: "Kurtköy Derecesi" }
  },
  {
    id: 2,
    badge: "ARENA ZAFERİ & TAKTİK",
    badgeIcon: Zap,
    title: "Yüksek İrtifa ve Kusursuz Takım Oyunuyla",
    highlight: "Kurtköy'de Podyumdayız!",
    location: "Kurtköy Pendik Spor Salonu, İstanbul",
    date: "Drone Futbolu Etabı",
    description: "Savunma ve hücum hattında uyguladığımız stratejik taktikler ve pilotlarımızın anlık refleksleri sayesinde zorlu rakipleri geride bırakıp dereceye uzandık.",
    stats: [
      { label: "Refleks", value: "Maksimum Hız" },
      { label: "Taktik", value: "Takım Koordinasyonu" },
      { label: "Başarı", value: "Podyum Gururu" }
    ],
    bgImage: droneImg5,
    droneAsset: musabaka2,
    accentColor: "#0f2d5e",
    overlayTag: { title: "Podyum Başarısı", subtitle: "Taktiksel Galibiyet" }
  },
  {
    id: 3,
    badge: "GENÇ YETENEKLER SAHADA",
    badgeIcon: Award,
    title: "Geleceğin Şampiyon Pilotları",
    highlight: "Kurtköy Arena'da Parladı!",
    location: "Kurtköy Pendik Spor Salonu, İstanbul",
    date: "Turnuva Finalleri",
    description: "Flying Slap altyapısından yetişen genç sporcularımız, Kurtköy'deki turnuvada sergiledikleri cesur uçuşlarla hem izleyicilerin hem de hakem heyetinin takdirini topladı.",
    stats: [
      { label: "Kategori", value: "Gençler & Açık Yaş" },
      { label: "Takım", value: "Flying Slap SK" },
      { label: "Motivasyon", value: "%100 İnanç" }
    ],
    bgImage: droneImg4,
    droneAsset: musabaka3,
    accentColor: "#e65100",
    overlayTag: { title: "8-5 Galibiyet", subtitle: "Kazanan Flying Slap" }
  },
  {
    id: 4,
    badge: "EĞİTİM & RESMİ ZİYARET",
    badgeIcon: Users,
    title: "Geleceğin Havacı Gençleri İçin",
    highlight: "İstanbul İl Millî Eğitim Müdürüyle Bir Araya Geldik!",
    location: "İstanbul İl Millî Eğitim Müdürlüğü",
    date: "Eğitim & İş Birliği Vizyonu",
    description: "Drone futbolunu okullarımızda yaygınlaştırmak, çocuklarımızı havacılık teknolojileriyle buluşturmak ve genç yetenekleri desteklemek amacıyla İstanbul İl Millî Eğitim Müdürü ile verimli bir toplantı gerçekleştirdik.",
    stats: [
      { label: "Ziyaret", value: "İl MEM Buluşması" },
      { label: "Hedef Kitle", value: "Okullar & Gençler" },
      { label: "Proje", value: "Drone Futbolu Eğitimi" }
    ],
    bgImage: droneImg6,
    droneAsset: musabaka4,
    accentColor: "#0f2d5e",
    overlayTag: { title: "Eğitim İş Birliği", subtitle: "İl MEM Ziyareti" }
  }
];

export const SuccessSlider: React.FC = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(1);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Otomatik geçiş
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setDirection(1);
      setCurrent((prev) => (prev + 1) % SLIDES.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [isPaused]);

  const handleNext = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % SLIDES.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      handleNext();
    } else if (diff < -50) {
      handlePrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const slide = SLIDES[current];
  const BadgeIcon = slide.badgeIcon;

  return (
    <section 
      className="relative w-full py-6 md:py-10 overflow-hidden bg-gradient-to-b from-white via-[#f8fafc] to-white"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        
        {/* Ana Kaydırma Kartı (Otomatik Kayan Slider Container) */}
        <div className="relative rounded-3xl md:rounded-[32px] overflow-hidden shadow-2xl border border-[#0f2d5e]/10 bg-[#0a192f]">
          
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, x: direction * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -direction * 40 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative min-h-[480px] sm:min-h-[520px] md:min-h-[500px] flex flex-col justify-between"
            >
              {/* Arka Plan Fotoğrafı */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 scale-105"
                style={{ backgroundImage: `url(${slide.bgImage})` }}
              />
              {/* Çok katmanlı gradient kaplama: okunabilirliği garanti eder */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f] via-[#0a192f]/85 to-[#0a192f]/40 md:bg-gradient-to-r md:from-[#0a192f] md:via-[#0a192f]/90 md:to-transparent" />
              <div className="absolute inset-0 bg-[#0f2d5e]/30 backdrop-blur-[2px]" />

              {/* İçerik Grid */}
              <div className="relative z-10 p-4 sm:p-6 md:p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-8 items-center h-full">
                
                {/* 1. MOBİLDE ÜSTTE (order-1), WEB'DE SAĞDA (lg:order-2): Müsabaka & Etkinlik Fotoğrafı */}
                <div className="order-1 lg:order-2 lg:col-span-5 flex items-center justify-center relative w-full">
                  
                  {/* Parlama Efekti */}
                  <div 
                    className="absolute w-44 h-44 sm:w-64 sm:h-64 rounded-full blur-3xl opacity-30 pointer-events-none"
                    style={{ background: slide.accentColor }}
                  />

                  {/* Fotoğraf Kartı */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative z-10 w-full max-w-[340px] sm:max-w-[400px] lg:max-w-[420px]"
                  >
                    <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden border border-white/25 sm:border-2 shadow-[0_15px_40px_rgba(0,0,0,0.6)] bg-black/50 group">
                      <img
                        src={slide.droneAsset}
                        alt={slide.title}
                        className="w-full h-[180px] xs:h-[200px] sm:h-[240px] md:h-[270px] lg:h-[290px] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/10 pointer-events-none" />
                      
                      {/* Canlı Kupa / Bilgi Rozeti Overlay */}
                      <motion.div 
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="absolute bottom-2.5 left-2.5 sm:bottom-3 sm:left-3 bg-white/95 backdrop-blur-md text-[#0f2d5e] px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-xl sm:rounded-2xl shadow-xl border border-white/50 flex items-center gap-2 sm:gap-2.5 z-10"
                      >
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-orange-100 flex items-center justify-center text-[#e65100] flex-shrink-0">
                          <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                        <div>
                          <p className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase leading-none">{slide.overlayTag.title}</p>
                          <p className="text-[11px] sm:text-xs font-black text-[#0f2d5e] leading-tight">{slide.overlayTag.subtitle}</p>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>

                </div>

                {/* 2. MOBİLDE ALTTA (order-2), WEB'DE SOLDA (lg:order-1): Metin ve Başarı Bilgileri */}
                <div className="order-2 lg:order-1 lg:col-span-7 flex flex-col justify-center">
                  
                  {/* Rozet & Konum */}
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2.5 sm:mb-4">
                    <span 
                      className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider text-white shadow-md"
                      style={{ background: slide.accentColor === '#e65100' ? '#e65100' : '#0284c7' }}
                    >
                      <BadgeIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      {slide.badge}
                    </span>
                    <span className="text-white/80 text-[10px] sm:text-xs font-semibold flex items-center gap-1 bg-white/10 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full backdrop-blur-md">
                      <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#e65100]" />
                      {slide.location}
                    </span>
                  </div>

                  {/* Başlık */}
                  <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.5rem] font-black font-headline text-white tracking-tight uppercase leading-[1.15] lg:leading-[1.1] mb-2 sm:mb-3">
                    <span>{slide.title}</span><br />
                    <span className="text-[#e65100] drop-shadow-md">{slide.highlight}</span>
                  </h3>

                  {/* Açıklama */}
                  <p className="text-gray-200 text-xs sm:text-sm md:text-base leading-relaxed mb-4 sm:mb-6 font-medium max-w-xl">
                    {slide.description}
                  </p>

                  {/* İstatistikler / Bilgi Kutucukları */}
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-4 mb-4 sm:mb-6 pt-3 sm:pt-4 border-t border-white/15 max-w-lg">
                    {slide.stats.map((st, i) => (
                      <div key={i} className="bg-white/10 backdrop-blur-md rounded-lg sm:rounded-xl p-2 sm:p-3 border border-white/10 text-center">
                        <span className="block text-[9px] sm:text-xs uppercase font-bold text-gray-300 tracking-wider mb-0.5 truncate">
                          {st.label}
                        </span>
                        <span className="block text-[11px] sm:text-sm font-black text-white truncate">
                          {st.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Butonlar */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <button
                      onClick={() => navigate('/yarisma-takvimi')}
                      className="flex-1 sm:flex-initial px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-full bg-[#e65100] hover:bg-[#d84315] text-white text-[11px] sm:text-sm font-bold tracking-wider uppercase transition-all shadow-lg hover:shadow-orange-500/30 flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer active:scale-95"
                    >
                      YARIŞMA TAKVİMİ <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => navigate('/hakkimizda')}
                      className="flex-1 sm:flex-initial px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-[11px] sm:text-sm font-bold tracking-wider uppercase transition-all border border-white/20 backdrop-blur-md cursor-pointer active:scale-95 text-center"
                    >
                      KULÜP HAKKINDA
                    </button>
                  </div>

                </div>

              </div>

              {/* Alt İlerleme Çubuğu, Noktalar & Kontroller */}
              <div className="relative z-20 px-6 sm:px-12 py-3.5 bg-black/40 backdrop-blur-md border-t border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold text-white/70 tracking-widest uppercase">CANLI AKIŞ:</span>
                  <div className="flex gap-1.5">
                    {SLIDES.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setDirection(i > current ? 1 : -1);
                          setCurrent(i);
                        }}
                        aria-label={`Slayt ${i + 1}`}
                        className={`h-2 rounded-full transition-all duration-500 cursor-pointer ${
                          i === current 
                            ? 'w-7 sm:w-10 bg-[#e65100]' 
                            : 'w-2 bg-white/30 hover:bg-white/60'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Sağ: Tarih & Ok Butonları */}
                <div className="flex items-center gap-4">
                  <div className="hidden sm:flex items-center gap-2 text-[11px] font-semibold text-white/70">
                    <Calendar className="w-3.5 h-3.5 text-[#e65100]" />
                    <span>{slide.date}</span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handlePrev}
                      aria-label="Önceki"
                      className="w-8 h-8 rounded-full bg-white/10 hover:bg-[#e65100] text-white flex items-center justify-center transition-all cursor-pointer active:scale-95"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleNext}
                      aria-label="Sonraki"
                      className="w-8 h-8 rounded-full bg-white/10 hover:bg-[#e65100] text-white flex items-center justify-center transition-all cursor-pointer active:scale-95"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

            </motion.div>
          </AnimatePresence>

          {/* Otomatik İlerleme Çizgisi */}
          <motion.div
            key={`progress-${current}`}
            initial={{ width: "0%" }}
            animate={{ width: isPaused ? undefined : "100%" }}
            transition={{ duration: 4.5, ease: "linear" }}
            className="absolute bottom-0 left-0 h-1 bg-[#e65100] z-30 pointer-events-none"
          />

        </div>

      </div>
    </section>
  );
};
